import * as THREE from 'three';

const blobTexture = (): THREE.Texture => {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  const gr = g.createRadialGradient(32, 32, 4, 32, 32, 32);
  gr.addColorStop(0, 'rgba(0,0,0,0.75)');
  gr.addColorStop(0.55, 'rgba(0,0,0,0.45)');
  gr.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
};

/** Soft contact shadows under every piece: grounds the miniatures on the table. One draw call. */
export class ShadowLayer {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;
  private m = new THREE.Matrix4();

  constructor(private cap = 1024) {
    const mat = new THREE.MeshBasicMaterial({ map: blobTexture(), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
  }

  begin(): void {
    this.n = 0;
  }

  add(x: number, y: number, z: number, size: number): void {
    if (this.n >= this.cap) return;
    this.m.makeScale(size, 1, size).setPosition(x, y + 0.012, z);
    this.mesh.setMatrixAt(this.n++, this.m);
  }

  get count(): number {
    return this.n;
  }

  /** The shadows added since `from`, to paste() again on a later fill. */
  copyFrom(from: number): Float32Array {
    return (this.mesh.instanceMatrix.array as Float32Array).slice(from * 16, this.n * 16);
  }

  /** Adds shadows copied with copyFrom(), as if added one by one (what doesn't fit is left out). */
  paste(m: Float32Array): void {
    const k = Math.min(m.length / 16, this.cap - this.n);
    if (k <= 0) return;
    (this.mesh.instanceMatrix.array as Float32Array).set(m.subarray(0, k * 16), this.n * 16);
    this.n += k;
  }

  end(): void {
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
